import { mergeAttributes, Node, ReactNodeViewRenderer } from "@tiptap/react";
import { CONTEXT_ATTR, CONTEXT_NODE_NAME } from "./constants";
import { ContextNodeView } from "./context-node";

export interface ContextNodeOptions {
  HTMLAttributes: Record<string, unknown>;
}

declare module "@tiptap/react" {
  interface Commands<ReturnType> {
    contextKey: {
      /** Insert a dynamic context node that resolves the given context variable. */
      insertContextKey: (contextKey: string) => ReturnType;
    };
  }
}

/**
 * Inline, atomic node representing a context-variable reference (device/org
 * characteristics + document metadata). Stateless: it stores only the variable id
 * and resolves its value live via the dynamic node context. Read-only in the
 * editor. Mirrors `DataKeyNode` but draws from the context map, not answers.
 */
export const ContextNode = Node.create<ContextNodeOptions>({
  name: CONTEXT_NODE_NAME,
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,

  addOptions() {
    return { HTMLAttributes: {} };
  },

  addAttributes() {
    return {
      [CONTEXT_ATTR]: {
        default: "",
        parseHTML: (element) => element.getAttribute(`data-${CONTEXT_ATTR}`) ?? "",
        renderHTML: (attributes) => ({
          [`data-${CONTEXT_ATTR}`]: attributes[CONTEXT_ATTR],
        }),
      },
    };
  },

  parseHTML() {
    return [{ tag: `span[data-type="${CONTEXT_NODE_NAME}"]` }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "span",
      mergeAttributes(
        { "data-type": CONTEXT_NODE_NAME },
        this.options.HTMLAttributes,
        HTMLAttributes
      ),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ContextNodeView);
  },

  addCommands() {
    return {
      insertContextKey:
        (contextKey: string) =>
        ({ commands }) =>
          commands.insertContent({
            type: this.name,
            attrs: { [CONTEXT_ATTR]: contextKey },
          }),
    };
  },
});

export default ContextNode;
